import React from 'react';
import { Alert, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import Loader from '../../../components/Loader';
import type { VinInspectionItem } from '../inspectionTypes';
import InspectionAssetsPanel from './InspectionAssetsPanel';
import type { InspectionAssetsResponse } from '../../../types/common/asset';
import type { ApiError } from '../../../types/api/network';
import type { InspectionMediaData, InspectionMediaType } from '../inspectionMediaTypes';
import type { InspectionAssetTabKey } from '../inspectionTabs';

interface InspectionAssetTabContentProps {
  inspection: VinInspectionItem;
  assetType: InspectionAssetTabKey;
  assets: InspectionAssetsResponse | null;
  loadingAssets: boolean;
  assetsLoaded: boolean;
  error: ApiError | Error | null;
  onRetry: () => void;
  onOpenModal?: (imageData?: InspectionMediaData, type?: InspectionMediaType) => void;
}

const InspectionAssetTabContent: React.FC<InspectionAssetTabContentProps> = ({
  inspection,
  assetType,
  assets,
  loadingAssets,
  assetsLoaded,
  error,
  onRetry,
  onOpenModal,
}) => {
  if (loadingAssets) return <Loader />;

  if (error) {
    return (
      <div className="p-4">
        <Alert
          message="Failed to load inspection assets"
          description={error?.message || 'An unexpected error occurred'}
          type="error"
          showIcon
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={onRetry} className="ml-2">
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (!assetsLoaded) {
    return <div className="text-center text-gray-400 py-10">No assets loaded. Click retry or try opening the panel again.</div>;
  }

  if (assetsLoaded && !assets?.items?.length) {
    return <div className="text-center text-gray-400 py-10">No assets found for this inspection.</div>;
  }

  return <InspectionAssetsPanel onOpenModal={onOpenModal} assets={assets} inspection={inspection} assetType={assetType} />;
};

export default InspectionAssetTabContent;
